import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  COMPETITION_STORAGE_KEY,
  DEFAULT_COMPETITION_SLUG
} from '../constants/competition';
import { AuthLayout } from '../components/layout/AuthLayout';
import { FormInput } from '../components/ui/FormInput';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import { Mail, Lock, LogIn } from 'lucide-react';

const Login = () => {
  const { login } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = await login(formData.email, formData.password, formData.rememberMe);
      if (success) {
        toast.success(language === 'en' ? 'Login successful!' : 'התחברות הצליחה!');
        try {
          const s = localStorage.getItem(COMPETITION_STORAGE_KEY) || DEFAULT_COMPETITION_SLUG;
          navigate(`/c/${s}/dashboard`);
        } catch {
          navigate(`/c/${DEFAULT_COMPETITION_SLUG}/dashboard`);
        }
      }
    } catch (error) {
      console.error('Unexpected login error:', error);
      toast.error(language === 'en' ? 'An unexpected error occurred' : 'אירעה שגיאה בלתי צפויה');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return (
    <AuthLayout
      title={t('welcome')}
      subtitle={language === 'en' ? 'Sign in to your account' : 'היכנס לחשבון שלך'}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <FormInput
          id="email"
          name="email"
          type="email"
          label={t('email')}
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder={language === 'en' ? 'Enter your email' : 'הכנס את האימייל שלך'}
          icon={<Mail className="h-5 w-5" />}
          isRTL={isRTL}
        />

        <FormInput
          id="password"
          name="password"
          type="password"
          label={t('password')}
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="current-password"
          placeholder={language === 'en' ? 'Enter your password' : 'הכנס את הסיסמה שלך'}
          icon={<Lock className="h-5 w-5" />}
          isRTL={isRTL}
          dir="ltr"
        />

        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            name="rememberMe"
            checked={formData.rememberMe}
            onChange={handleChange}
            className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm font-medium text-zinc-700">
            {language === 'en' ? 'Remember me for 30 days' : 'זכור אותי למשך 30 יום'}
          </span>
        </label>

        <Button type="submit" size="lg" loading={loading} className="w-full">
          <LogIn className="h-5 w-5" />
          {loading
            ? language === 'en'
              ? 'Signing in…'
              : 'מתחבר…'
            : t('signIn')}
        </Button>
      </form>

      <p className="mt-6 border-t border-zinc-100 pt-6 text-center text-sm text-zinc-600">
        {t('dontHaveAccount')}{' '}
        <Link to="/register" className="font-semibold text-brand-600 hover:text-brand-700">
          {t('signUp')}
        </Link>
      </p>
    </AuthLayout>
  );
};

export default Login;
